import { useReducer, useCallback } from "react"

export interface WizardFeatures {
  hasSkylights: boolean
  skylightCount: number
  hasMultipleStories: boolean
  hasChimney: boolean
  hasAsbestos: boolean
}

export interface WizardContact {
  firstName: string
  lastName: string
  email: string
  phone: string
  consent: boolean
}

export interface WizardState {
  step: number
  address: string
  formattedAddress: string
  lat: number | null
  lng: number | null
  roofImageUrl: string | null
  roofPolygon: Array<{ lat: number; lng: number }>
  roofSizeSqft: number | null
  complexity: "simple" | "normal" | "complex" | null
  features: WizardFeatures
  detachedGarageSize: "none" | "small" | "medium" | "large"
  timeline: string | null
  paymentMethod: string | null
  selectedMaterial: string
  contact: WizardContact
  estimate: any | null
  loading: boolean
  error: string | null
}

type WizardAction =
  | { type: "SET_ADDRESS"; address: string; formattedAddress: string; lat: number; lng: number }
  | { type: "SET_ROOF_IMAGE"; url: string }
  | { type: "SET_ROOF_DATA"; polygon: Array<{ lat: number; lng: number }>; sizeSqft: number }
  | { type: "SET_COMPLEXITY"; complexity: "simple" | "normal" | "complex" }
  | { type: "SET_FEATURES"; features: Partial<WizardFeatures> }
  | { type: "SET_GARAGE"; size: "none" | "small" | "medium" | "large" }
  | { type: "SET_TIMELINE"; timeline: string }
  | { type: "SET_PAYMENT"; method: string }
  | { type: "SET_MATERIAL"; material: string }
  | { type: "SET_CONTACT"; contact: Partial<WizardContact> }
  | { type: "SET_ESTIMATE"; estimate: any }
  | { type: "SET_LOADING"; loading: boolean }
  | { type: "SET_ERROR"; error: string | null }
  | { type: "NEXT_STEP" }
  | { type: "PREV_STEP" }
  | { type: "GO_TO_STEP"; step: number }
  | { type: "RESET" }

const initialState: WizardState = {
  step: 1,
  address: "",
  formattedAddress: "",
  lat: null,
  lng: null,
  roofImageUrl: null,
  roofPolygon: [],
  roofSizeSqft: null,
  complexity: null,
  features: {
    hasSkylights: false,
    skylightCount: 2,
    hasMultipleStories: false,
    hasChimney: false,
    hasAsbestos: false,
  },
  detachedGarageSize: "none",
  timeline: null,
  paymentMethod: null,
  selectedMaterial: "Timberline HDZ",
  contact: { firstName: "", lastName: "", email: "", phone: "", consent: false },
  estimate: null,
  loading: false,
  error: null,
}

function wizardReducer(state: WizardState, action: WizardAction): WizardState {
  switch (action.type) {
    case "SET_ADDRESS":
      return { ...state, address: action.address, formattedAddress: action.formattedAddress, lat: action.lat, lng: action.lng }
    case "SET_ROOF_IMAGE":
      return { ...state, roofImageUrl: action.url }
    case "SET_ROOF_DATA":
      return { ...state, roofPolygon: action.polygon, roofSizeSqft: action.sizeSqft }
    case "SET_COMPLEXITY":
      return { ...state, complexity: action.complexity }
    case "SET_FEATURES":
      return { ...state, features: { ...state.features, ...action.features } }
    case "SET_GARAGE":
      return { ...state, detachedGarageSize: action.size }
    case "SET_TIMELINE":
      return { ...state, timeline: action.timeline }
    case "SET_PAYMENT":
      return { ...state, paymentMethod: action.method }
    case "SET_MATERIAL":
      return { ...state, selectedMaterial: action.material }
    case "SET_CONTACT":
      return { ...state, contact: { ...state.contact, ...action.contact } }
    case "SET_ESTIMATE":
      return { ...state, estimate: action.estimate, loading: false }
    case "SET_LOADING":
      return { ...state, loading: action.loading, error: null }
    case "SET_ERROR":
      return { ...state, error: action.error, loading: false }
    case "NEXT_STEP":
      return { ...state, step: Math.min(state.step + 1, 8) }
    case "PREV_STEP":
      return { ...state, step: Math.max(state.step - 1, 1) }
    case "GO_TO_STEP":
      return { ...state, step: action.step }
    case "RESET":
      return { ...initialState }
    default:
      return state
  }
}

export function useWizardState() {
  const [state, dispatch] = useReducer(wizardReducer, initialState)

  const setAddress = useCallback((address: string, formattedAddress: string, lat: number, lng: number) => {
    dispatch({ type: "SET_ADDRESS", address, formattedAddress, lat, lng })
  }, [])

  const setRoofImage = useCallback((url: string) => {
    dispatch({ type: "SET_ROOF_IMAGE", url })
  }, [])

  const setRoofData = useCallback((polygon: Array<{ lat: number; lng: number }>, sizeSqft: number) => {
    dispatch({ type: "SET_ROOF_DATA", polygon, sizeSqft })
  }, [])

  const setComplexity = useCallback((complexity: "simple" | "normal" | "complex") => {
    dispatch({ type: "SET_COMPLEXITY", complexity })
  }, [])

  const setFeatures = useCallback((features: Partial<WizardFeatures>) => {
    dispatch({ type: "SET_FEATURES", features })
  }, [])

  const setGarage = useCallback((size: "none" | "small" | "medium" | "large") => {
    dispatch({ type: "SET_GARAGE", size })
  }, [])

  const setTimeline = useCallback((timeline: string) => {
    dispatch({ type: "SET_TIMELINE", timeline })
  }, [])

  const setPayment = useCallback((method: string) => {
    dispatch({ type: "SET_PAYMENT", method })
  }, [])

  const setMaterial = useCallback((material: string) => {
    dispatch({ type: "SET_MATERIAL", material })
  }, [])

  const setContact = useCallback((contact: Partial<WizardContact>) => {
    dispatch({ type: "SET_CONTACT", contact })
  }, [])

  const setEstimate = useCallback((estimate: any) => {
    dispatch({ type: "SET_ESTIMATE", estimate })
  }, [])

  const setLoading = useCallback((loading: boolean) => {
    dispatch({ type: "SET_LOADING", loading })
  }, [])

  const setError = useCallback((error: string | null) => {
    dispatch({ type: "SET_ERROR", error })
  }, [])

  const nextStep = useCallback(() => dispatch({ type: "NEXT_STEP" }), [])
  const prevStep = useCallback(() => dispatch({ type: "PREV_STEP" }), [])
  const goToStep = useCallback((step: number) => dispatch({ type: "GO_TO_STEP", step }), [])
  const reset = useCallback(() => dispatch({ type: "RESET" }), [])

  return {
    state,
    setAddress,
    setRoofImage,
    setRoofData,
    setComplexity,
    setFeatures,
    setGarage,
    setTimeline,
    setPayment,
    setMaterial,
    setContact,
    setEstimate,
    setLoading,
    setError,
    nextStep,
    prevStep,
    goToStep,
    reset,
  }
}
